import React from 'react'
import { useState, useEffect } from 'react'
import moment from 'moment'
import { Fragment } from 'react'
import { Dialog, DialogPanel, DialogTitle, Transition } from '@headlessui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark, faPen, faPenToSquare } from '@fortawesome/free-solid-svg-icons'
import { useNavigate,} from 'react-router-dom'
import useAxiosPrivate from '../hooks/useAxiosPrivate'
import useAuth from '../hooks/useAuth'

const SessionExtensionSlideOut = ({ open, setOpen, notificationData, onStatusUpdate  }) => {
  const navigate = useNavigate()
  const axios = useAxiosPrivate()
  const [status, setStatus] = useState('')
  const { setShowLoader, showLoader, createInfo} = useAuth()
  const [editingStatus, setEditingStatus] = useState(false)
  const [customStatus, setCustomStatus] = useState('')
  const [showFullReason, setShowFullReason] = useState(false)

  useEffect(() => {
    if (notificationData?.meta?.status) {
      setStatus(notificationData.meta.status.toLowerCase())
    }
  }, [notificationData])

  if (!notificationData) {
    return null;
  }

  const statusOptions = [
    'Pending',
    'Approval Requested',
    'Policy Expired/Awaiting Renewal',
    'Other'
  ]
  
  const formatDate = (dateString) => {
    return moment(dateString).format('DD/MM/YYYY');
  }

  const viewClient = (clientId, claimId = null, sessionId = null) => {

    if (sessionId) {
      navigate(`/clients/${clientId}/claims/${claimId}/sessions/${sessionId}`)
    } else {
      navigate(`/clients/${clientId}`)
    }
  }

  const getExpiryBadge = (expiryDate) => {
    const now = moment()
    if (!expiryDate) return null
    const expiry = moment(expiryDate)
  
    const diffInDays = expiry.diff(now, 'days')
    const diffInWeeks = expiry.diff(now, 'weeks')
  
    if (expiry.isBefore(now, 'day')) {
      return <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm">Expired</span>
    }
  
    if (diffInDays <= 7) {
      return (
        <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm">
          Expires in {diffInDays} Day{diffInDays !== 1 ? 's' : ''}
        </span>
      )
    }
  
    if (diffInWeeks < 5) {
      return (
        <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
          Expires in {diffInWeeks} Week{diffInWeeks !== 1 ? 's' : ''}
        </span>
      )
    }
  
    return null
  }

  const updateExtensionStatus = async ({
    id,
    status,
    setStatus,
    setEditingStatus,
    setCustomStatus,
    createInfo,
    setShowLoader,
    onStatusUpdate,
  }) => {
    try {
      setShowLoader(true)
      await axios.post(`notifications/session-extension/update-status/${id}`, {
        status,
      })
      setStatus(status)
      setEditingStatus(false)
      if (setCustomStatus) setCustomStatus('')
      if (onStatusUpdate) {
        onStatusUpdate(id, status)
      }

      createInfo('success', 'Successfully Updated Status')
    } catch (error) {
      console.error('Failed to update status:', error)
      createInfo('error', 'The status could not be updated')
    } finally {
      setShowLoader(false)
    }
  }

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative" onClose={setOpen} style={{zIndex: 999}}>
        <Transition
          show={open}
          as={Fragment}
          enter="ease-in-out duration-75"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-75"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <Transition
                show={open}
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <DialogPanel className="pointer-events-auto w-screen max-w-2xl">
                  <div className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
                    <div className="bg-blue-500 px-4 py-6 sm:px-6">
                      <div className="flex items-center justify-between">
                        <DialogTitle className="text-base font-semibold leading-6 text-white">Title: {notificationData.title}</DialogTitle>
                        <div className="ml-3 flex h-7 items-center">
                          <button
                            type="button"
                            className="relative rounded-md bg-blue-500 text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                            onClick={() => setOpen(false)}
                          >
                            <span className="absolute -inset-2.5" />
                            <span className="sr-only">Close panel</span>
                            <FontAwesomeIcon icon={faXmark} />
                          </button>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="mb-3 p-6">
                        <div className="space-y-1">
                          <div className='pb-4'><div className='font-medium'>Description: </div><span className='text-slate-600'>{notificationData.body}</span></div>
                          <div className='grid grid-cols-2 space-y-1'>
                            <div><div className='font-medium'>Client Reference: </div><span className='text-slate-600'>{notificationData.meta?.client_reference}</span></div>
                            <div><div className='font-medium'>Claim Reference: </div><span className='text-slate-600'>{notificationData.meta?.claim_reference}</span></div>
                            <div><div className='font-medium'>Request Date: </div><span className='text-slate-600'>{notificationData.meta?.requested_at ? formatDate(notificationData.meta?.requested_at) : ''}</span> </div>
                            <div><div className='font-medium'>Requested By: </div><span className='text-slate-600'>{notificationData.created}</span></div>
                            {notificationData.meta?.hasOwnProperty('is_insured') && (
                              <div>
                                <div className="font-medium">Is Insured:</div>
                                <span className="text-slate-600">
                                  {notificationData.meta.is_insured ? 'Yes' : 'No'}
                                </span>
                              </div>
                            )}
                            {notificationData.meta?.policy_end_date && (
                              <div><div className='font-medium'>Policy Expiry: </div>
                                <span className='text-slate-600 pr-2'>{formatDate(notificationData.meta?.policy_end_date) ?? ''}</span>
                                <span className='text-xs'>{getExpiryBadge(notificationData.meta.policy_end_date)}</span>
                              </div>
                            )}
                          
                            <div className='flex items-center justify-between col-span-2'>
                              {notificationData.meta?.external_reference && (
                                <div><div className='font-medium'>CSN: </div><span className='text-slate-600'>{notificationData.meta?.external_reference}</span></div>
                              )}
                              {notificationData.meta?.session_start_time && (
                                <div className='w-1/2'>
                                  <div className='font-medium'>Session Start Date:</div>
                                  <span className='text-slate-600'>{moment(notificationData.meta.session_start_time).format('hh:mm DD/MM/YYYY')}</span>
                                </div>
                              )}
                            </div>
                         
                            {notificationData.meta?.status && (
                              <div className='flex items-center justify-between col-span-2'>
                                <div>
                                  <div className='font-medium flex items-center'>
                                    Status:
                                    {!editingStatus && (
                                      <button
                                        className='ml-2 text-slate-500 hover:text-slate-800'
                                        onClick={() => setEditingStatus(true)}
                                      >
                                        <FontAwesomeIcon icon={faPenToSquare} />
                                      </button>
                                    )}
                                  </div>

                                  {!editingStatus ? (
                                    <span
                                      className={`px-3 py-1 rounded-full text-white text-sm font-medium capitalize mt-1 inline-block
                                        ${
                                          status === 'pending'
                                            ? 'bg-orange-500'
                                            : status === 'approval requested'
                                            ? 'bg-green-500'
                                            : status === 'policy expired' || status === 'awaiting renewal'
                                            ? 'bg-red-500'
                                            : 'bg-blue-500'
                                        }`}
                                    >
                                      {status}
                                    </span>
                                  ) : (
                                    <div className="mt-2">
                                      <select
                                        value={status}
                                        onChange={(e) => setStatus(e.target.value)}
                                        className="border rounded px-2 py-1 text-sm"
                                      >
                                        {statusOptions.map((option) => (
                                          <option key={option} value={option.toLowerCase()}>
                                            {option}
                                          </option>
                                        ))}
                                      </select>

                                      {status === 'other' && (
                                        <input
                                          type="text"
                                          placeholder="Enter custom status"
                                          value={customStatus}
                                          onChange={(e) => setCustomStatus(e.target.value)}
                                          className="mt-2 block w-full border rounded px-2 py-1 text-sm"
                                        />
                                      )}

                                      <div className="flex mt-2 gap-2">
                                        <button
                                          onClick={() => {
                                            const finalStatus = status === 'other' ? customStatus : status

                                            updateExtensionStatus({
                                              id: notificationData.id,
                                              status: finalStatus,
                                              setStatus,
                                              setEditingStatus,
                                              setCustomStatus,
                                              createInfo,
                                              setShowLoader,
                                              onStatusUpdate
                                            })
                                          }}
                                          className="btn btn-sm blue"
                                        >
                                          Save
                                        </button>
                                        <button
                                          onClick={() => {
                                            setEditingStatus(false)
                                            setCustomStatus('')
                                            setStatus(notificationData.meta?.status?.toLowerCase() || 'pending')
                                          }}
                                          className="btn btn-sm red"
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            {notificationData.meta?.reason_for_extension && (
                              <div className='col-span-2 pt-2 h-60 overflow-y-scroll'>
                                <div className='font-medium'>Extension Reason:</div>
                                <span className='text-slate-600'>
                                  {notificationData.meta.reason_for_extension.length > 500 && !showFullReason
                                    ? `${notificationData.meta.reason_for_extension.slice(0, 500)}...`
                                    : notificationData.meta.reason_for_extension}
                                </span>
                                {notificationData.meta.reason_for_extension.length > 500 && (
                                  <button
                                    onClick={() => setShowFullReason(!showFullReason)}
                                    className="text-blue-600 text-sm ml-2 underline"
                                  >
                                    {showFullReason ? 'Show Less' : 'Show More'}
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex mt-2 w-full absolute bottom-6">
                          {notificationData.meta?.session_id ? (
                            <button onClick={() => viewClient(notificationData.meta?.client_id, notificationData.meta?.claim_id, notificationData.meta?.session_id)}  className="btn mt-4 mr-4 w-1/2">
                              <span>
                                View Session
                              </span>
                            </button>
                          ) : (
                            <button onClick={() => viewClient(notificationData.meta?.client_id)} className="btn mt-4 mr-4 w-1/2">
                              <span>
                                View Client
                              </span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </DialogPanel>
              </Transition>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default SessionExtensionSlideOut
